import { makeObservable, observable } from 'mobx';
import { notify } from 'messages';
import { confirmDialog } from 'dialogs';
import type { Received } from 'validation';
import type { EditableEntity } from './EditableEntity';
import { toError } from 'helpers/toError';

/**
 * Caller-provided messages for the saving toasts. Kept simple — fired
 * once per `submit()` outcome. If a caller needs entity-aware variants
 * (e.g. "created" vs "updated"), it can construct messages at the call
 * site that builds the EditModel.
 */
export interface EditModelOptions {
  /**
   * Whether the dialog opens in create or edit mode. Used as the
   * fallback for `EditModel.isCreating` while the entity is still
   * loading — once the entity is available the model defers to it.
   */
  readonly initialMode: 'create' | 'edit';
  readonly successMessage: string;
  readonly errorMessagePrefix?: string;
}

/**
 * Generic edit-lifecycle orchestrator for an `EditableEntity<D>`.
 *
 * Holds two orthogonal state observables (both `observable.ref` — they
 * store sentinel literals alongside the entity / Error, which MobX must
 * not proxy):
 * - `_state`: 'loading' | T | Error — what the editable IS at this
 *   moment, from the load lifecycle's perspective.
 * - `_savingState`: 'idle' | 'saving' | 'out-of-sync' | Error — what the
 *   editable is DOING in the save lifecycle. Mutating this is independent
 *   of `_state`; while a save is in flight, the entity is still readable.
 *
 * A save is two operations — write, then reload from the receipt — so it
 * has three outcomes, not two: keep editing, done, and **out of sync**
 * (issue #1035). Out-of-sync is not an error: the write landed. It means
 * we no longer know what the server holds, so the entity on screen must
 * not be saved again — for a create that is the duplicate-record path.
 *
 * Both are written only internally and exposed through readonly getters.
 *
 * Errors surface differently per the UI's choice: load errors are
 * exposed via `loadError` for inline display; save success/error are
 * announced through `notify` directly here.
 *
 * The entity is constructed lazily via `entityFactory(data)` after the
 * initialize promise resolves, so the caller doesn't need to invent a
 * pre-load placeholder data value.
 *
 * Load is kicked off from the constructor and exposed as a `loaded`
 * Promise so tests can await readiness and the modal doesn't need to
 * remember to start the load itself.
 */
export class EditModel<T extends EditableEntity<Loaded>, Loaded> {
  private readonly _initialize: () => Promise<Loaded>;
  private readonly _save: (entity: T) => Promise<Received<Loaded>>;
  private readonly _entityFactory: (loaded: Loaded) => T;
  private readonly _options: EditModelOptions;
  private _state: T | 'loading' | Error = 'loading';
  private _savingState: 'idle' | 'saving' | 'out-of-sync' | Error = 'idle';

  readonly loaded: Promise<void>;

  constructor(
    initialize: () => Promise<Loaded>,
    save: (entity: T) => Promise<Received<Loaded>>,
    entityFactory: (loaded: Loaded) => T,
    options: EditModelOptions
  ) {
    this._initialize = initialize;
    this._save = save;
    this._entityFactory = entityFactory;
    this._options = options;
    makeObservable<EditModel<T, Loaded>, '_state' | '_savingState'>(this, {
      _state: observable.ref,
      _savingState: observable.ref,
    });
    this.loaded = this._load();
  }

  get entity(): T | null {
    const s = this._state;
    return s === 'loading' || s instanceof Error ? null : s;
  }

  /**
   * Whether the dialog is currently in create mode. Once the entity
   * has loaded the model delegates to `entity.isCreating` (so a future
   * Save-without-Close flips this to false after a successful save).
   * Before that, falls back to the `initialMode` option.
   */
  get isCreating(): boolean {
    const entity = this.entity;
    return entity ? entity.isCreating : this._options.initialMode === 'create';
  }

  get isLoading(): boolean {
    return this._state === 'loading';
  }

  /**
   * The write landed but its receipt could not be read, so what the server
   * holds is unknown. Deliberately NOT reported through `savingError`: the
   * save did not fail, and a caller that treats it as a failure will invite
   * the user to retry a change that is already persisted.
   *
   * A dialog that stays OPEN after saving MUST check this and stop accepting
   * edits — saving again from a state whose server identity we could not read
   * is the dangerous move. A save-and-close dialog can ignore it beyond the
   * message, because closing and refetching shows the truth anyway.
   */
  get isOutOfSync(): boolean {
    return this._savingState === 'out-of-sync';
  }

  get isSaving(): boolean {
    return this._savingState === 'saving';
  }

  get loadError(): Error | null {
    const s = this._state;
    return s instanceof Error ? s : null;
  }

  get savingError(): Error | null {
    const s = this._savingState;
    return s instanceof Error ? s : null;
  }

  get state(): T | 'loading' | Error {
    return this._state;
  }

  /**
   * The "Close" button / Esc / backdrop handler. If the entity hasn't
   * loaded or has no unsaved changes, closes immediately. Otherwise
   * asks the user to confirm discarding their edits and closes only on
   * confirmation. The caller passes its own `close` callback (usually
   * the `cancel` from `waitDialog`) so the model stays presentation-
   * agnostic about HOW dialogs close.
   */
  cancel = async (close: () => void): Promise<void> => {
    const entity = this.entity;
    if (!entity || !entity.isDirty) {
      close();
      return;
    }
    const discard = await confirmDialog({
      title: 'Unsaved Changes',
      body: 'You have unsaved changes. Are you sure you want to close without saving?',
      confirmText: 'Discard Changes',
      confirmVariant: 'danger',
    });
    if (discard) close();
  };

  /**
   * The "Save and Close" button handler. Submits and, only on success,
   * invokes `close`. On failure the dialog stays open with the error
   * toast already fired by `submit()`.
   */
  saveAndClose = async (close: () => void): Promise<void> => {
    await this.submit();
    if (this.savingError === null) close();
  };

  /**
   * Save the current entity (the injected `save` callback reads whatever it
   * needs off it — request body, original identity). No-ops if the entity
   * hasn't loaded yet OR if a save is already in flight (concurrency guard).
   * On success: reinitializes the entity with the server's response and fires
   * the success message. On failure: stores the error in `_savingState`
   * and announces the failure.
   *
   * When the write succeeded but its receipt could not be read in full, reports
   * it as a qualified success rather than a failure — because it IS one — and
   * leaves the entity un-reinitialized, since there is nothing trustworthy to
   * reinitialize it from. A DEGRADED receipt counts: reinitializing from one
   * seeds the form with our substitute for a field the server holds differently,
   * and the next save would write that substitute back.
   */
  submit = async (): Promise<void> => {
    const state = this._state;
    if (state === 'loading' || state instanceof Error) return;
    if (this._savingState === 'saving') return;

    this._savingState = 'saving';
    try {
      const saved = await this._save(state);
      if (saved.result !== 'ok') {
        this._savingState = 'out-of-sync';
        notify.warn(`${this._options.successMessage}, but the server's response could not be read — reopen the record to confirm what was saved.`);
        return;
      }
      state.reinitialize(saved.value);
      this._savingState = 'idle';
      notify.success(this._options.successMessage);
    } catch (err) {
      const errObj = toError(err);
      this._savingState = errObj;
      const prefix = this._options.errorMessagePrefix ?? 'Save failed';
      notify.error(`${prefix}: ${errObj.message}`);
    }
  };

  private _load = async (): Promise<void> => {
    try {
      const loaded = await this._initialize();
      this._state = this._entityFactory(loaded);
    } catch (err) {
      this._state = toError(err);
    }
  };
}
